/** @module @liquidefy/code-editor/circuits/smart-terminal @version 1.0.0 @license LQD Research LLC */

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import type {
  TerminalState,
  ShellType,
  SkillLevel,
} from '../code-editor-types.js';

import { ok, err, generateId, now } from '../code-editor.js';

import { createTerminal } from '../terminal/terminal.js';
import { createTerminalProfile } from '../terminal/terminal.js';

import type { CommandHint, HintSeverity } from '../terminal/guided/hints.js';
import type { CommandPreview } from '../terminal/guided/preview.js';
import type { PaletteEntry } from '../terminal/guided/palette.js';

import type { OnboardingState } from '../terminal/welcome/onboarding.js';
import type { QuickAction } from '../terminal/welcome/actions.js';
import type { OutputDigest } from '../terminal/welcome/digest.js';


// ═══════════════════════════════════════════════════════════════════════════════
// §1 CONFIG & HANDLE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

export interface SmartTerminalConfig {
  readonly name?: string;
  readonly shell?: ShellType;
  readonly colorScheme?: 'dark' | 'light';
  readonly skillLevel?: SkillLevel;
  readonly enableHints?: boolean;
  readonly enablePreviews?: boolean;
  readonly enableDigest?: boolean;
}

export interface ExecuteResult {
  readonly digest: OutputDigest;
  readonly hints: CommandHint[];
  readonly preview: CommandPreview;
}

export interface NLTranslation {
  readonly command: string;
  readonly confidence: number;
}

export interface SmartTerminalHandle extends CircuitHandle {
  getState(): InternalTerminalSnapshot;
  executeCommand(command: string): ExecuteResult;
  getHint(command: string): CommandHint | null;
  getPreview(command: string): CommandPreview;
  translateNL(input: string): NLTranslation;
  searchPalette(query: string): PaletteEntry[];
  getQuickActions(context?: { projectType?: string }): QuickAction[];
  digestOutput(command: string, output: string, exitCode: number): OutputDigest;
  getOnboardingState(): OnboardingState;
  advanceTutorial(command: string): { matched: boolean; explanation: string };
  getSkillLevel(): SkillLevel;
  switchTheme(scheme: 'dark' | 'light'): void;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §2 INTERNAL TYPES & STATE
// ═══════════════════════════════════════════════════════════════════════════════

export interface InternalTerminalSnapshot {
  readonly name: string;
  readonly shell: ShellType;
  readonly colorScheme: 'dark' | 'light';
  readonly skillLevel: SkillLevel;
  readonly commandHistory: string[];
  readonly commandCount: number;
  readonly sessionStart: number;
}

interface CommandKnowledge {
  readonly command: string;
  readonly description: string;
  readonly category: string;
  readonly riskLevel: 'safe' | 'moderate' | 'dangerous';
  readonly flags: Array<{ flag: string; description: string }>;
}

interface InternalState {
  name: string;
  shell: ShellType;
  colorScheme: 'dark' | 'light';
  skillLevel: SkillLevel;
  enableHints: boolean;
  enablePreviews: boolean;
  enableDigest: boolean;
  commandHistory: string[];
  uniqueCommands: Set<string>;
  commandCount: number;
  sessionStart: number;
  onboarding: OnboardingState;
  tutorialStep: number;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §3 KNOWLEDGE BASE — command hints, NL mappings, palette entries
// ═══════════════════════════════════════════════════════════════════════════════

const COMMAND_DB: CommandKnowledge[] = [
  { command: 'ls', description: 'List directory contents', category: 'files', riskLevel: 'safe', flags: [{ flag: '-la', description: 'Long format with hidden files' }, { flag: '-R', description: 'Recursive listing' }] },
  { command: 'cd', description: 'Change directory', category: 'navigation', riskLevel: 'safe', flags: [{ flag: '..', description: 'Go up one directory' }, { flag: '-', description: 'Return to previous directory' }] },
  { command: 'git', description: 'Version control system', category: 'git', riskLevel: 'safe', flags: [{ flag: 'status', description: 'Show working tree status' }, { flag: 'log', description: 'Show commit logs' }, { flag: 'diff', description: 'Show changes' }] },
  { command: 'npm', description: 'Node package manager', category: 'dependencies', riskLevel: 'safe', flags: [{ flag: 'install', description: 'Install dependencies' }, { flag: 'run', description: 'Run a script' }, { flag: 'test', description: 'Run tests' }] },
  { command: 'rm', description: 'Remove files or directories', category: 'files', riskLevel: 'dangerous', flags: [{ flag: '-r', description: 'Recursive removal' }, { flag: '-f', description: 'Force removal without confirmation' }] },
  { command: 'docker', description: 'Container management', category: 'docker', riskLevel: 'moderate', flags: [{ flag: 'ps', description: 'List containers' }, { flag: 'build', description: 'Build an image' }, { flag: 'run', description: 'Run a container' }] },
  { command: 'grep', description: 'Search text using patterns', category: 'search', riskLevel: 'safe', flags: [{ flag: '-r', description: 'Recursive search' }, { flag: '-i', description: 'Case insensitive' }, { flag: '-n', description: 'Show line numbers' }] },
  { command: 'cat', description: 'Display file contents', category: 'files', riskLevel: 'safe', flags: [{ flag: '-n', description: 'Number output lines' }] },
  { command: 'chmod', description: 'Change file permissions', category: 'files', riskLevel: 'moderate', flags: [{ flag: '+x', description: 'Add execute permission' }, { flag: '-R', description: 'Recursive' }] },
  { command: 'curl', description: 'Transfer data from/to a server', category: 'network', riskLevel: 'moderate', flags: [{ flag: '-X', description: 'Request method' }, { flag: '-H', description: 'Add header' }, { flag: '-d', description: 'Send data' }] },
  { command: 'find', description: 'Search for files in directory hierarchy', category: 'search', riskLevel: 'safe', flags: [{ flag: '-name', description: 'Match filename pattern' }, { flag: '-type', description: 'Filter by type' }] },
  { command: 'mkdir', description: 'Create directories', category: 'files', riskLevel: 'safe', flags: [{ flag: '-p', description: 'Create parent directories as needed' }] },
];

const NL_PATTERNS: Array<{ pattern: RegExp; command: string; confidence: number }> = [
  { pattern: /list\s*(all\s*)?files/i, command: 'ls -la', confidence: 0.95 },
  { pattern: /show\s*(me\s*)?files/i, command: 'ls', confidence: 0.9 },
  { pattern: /what('s| is)\s*(in|inside)\s*(this|the)\s*directory/i, command: 'ls -la', confidence: 0.9 },
  { pattern: /go\s*(to|into)\s+(.+)/i, command: 'cd $2', confidence: 0.85 },
  { pattern: /search\s*(for\s*)?(.+)\s*in\s*files/i, command: 'grep -r "$2" .', confidence: 0.8 },
  { pattern: /find\s*(a\s*)?file\s*(named|called)\s+(.+)/i, command: 'find . -name "$3"', confidence: 0.85 },
  { pattern: /show\s*(git\s*)?status/i, command: 'git status', confidence: 0.95 },
  { pattern: /install\s*(the\s*)?dependencies/i, command: 'npm install', confidence: 0.9 },
  { pattern: /run\s*(the\s*)?tests/i, command: 'npm test', confidence: 0.9 },
  { pattern: /create\s*(a\s*)?(new\s*)?folder\s*(named|called)?\s+(.+)/i, command: 'mkdir -p $4', confidence: 0.85 },
  { pattern: /delete\s*(the\s*)?file\s+(.+)/i, command: 'rm $2', confidence: 0.75 },
  { pattern: /show\s*(me\s*)?what\s*(changed|modified)/i, command: 'git diff', confidence: 0.85 },
  { pattern: /build\s*(the\s*)?(project|app)/i, command: 'npm run build', confidence: 0.85 },
  { pattern: /start\s*(the\s*)?(dev\s*)?(server)?/i, command: 'npm run dev', confidence: 0.8 },
  { pattern: /show\s*(recent\s*)?commits/i, command: 'git log --oneline -20', confidence: 0.9 },
];

function lookupCommand(command: string): CommandKnowledge | null {
  const base = command.trim().split(/\s+/)[0];
  return COMMAND_DB.find(c => c.command === base) ?? null;
}

function buildHint(command: string, kind: 'pre-execution' | 'post-success' | 'post-error', skillLevel: SkillLevel): CommandHint | null {
  const knowledge = lookupCommand(command);
  if (!knowledge) return null;

  const severity: HintSeverity = knowledge.riskLevel === 'dangerous' ? 'warning'
    : knowledge.riskLevel === 'moderate' ? 'suggestion'
    : 'info';

  let message = knowledge.description;
  if (knowledge.riskLevel === 'dangerous' && kind === 'pre-execution') {
    message = `Warning: ${knowledge.description} — this command can cause data loss`;
  }

  if (skillLevel === 'expert' && severity === 'info') return null;

  return {
    command: knowledge.command,
    type: kind,
    severity,
    title: message,
    body: knowledge.description,
    dismissed: false,
  };
}

function buildPreview(command: string): CommandPreview {
  const knowledge = lookupCommand(command);
  const parts = command.trim().split(/\s+/);
  const base = parts[0] ?? command;

  return {
    type: 'none',
    command: base,
    summary: knowledge?.description ?? 'Unknown command',
    details: parts.slice(1),
    affectedPaths: [],
    riskLevel: knowledge?.riskLevel ?? 'safe',
    canDryRun: ['rm', 'mv', 'cp'].includes(base),
    estimatedDuration: knowledge?.riskLevel === 'dangerous' ? 'variable' : 'fast',
  };
}

function buildDigest(command: string, output: string, exitCode: number): OutputDigest {
  const lines = output.split('\n').filter(Boolean);
  const base = command.trim().split(/\s+/)[0];

  if (exitCode !== 0) {
    return {
      type: 'general-error',
      headline: `Command failed with exit code ${exitCode}`,
      status: 'error',
      details: [
        { label: 'Command', value: command, type: 'text' },
        { label: 'Exit Code', value: exitCode, type: 'number', status: 'error' },
        { label: 'Output Lines', value: lines.length, type: 'number' },
      ],
      rawOutput: output,
      showRaw: true,
    };
  }

  if (base === 'npm' && command.includes('test')) {
    const passMatch = output.match(/(\d+)\s*pass/i);
    const failMatch = output.match(/(\d+)\s*fail/i);
    return {
      type: 'test-results',
      headline: failMatch ? `Tests: ${failMatch[1]} failed` : `All tests passed`,
      status: failMatch ? 'error' : 'success',
      details: [
        { label: 'Passed', value: passMatch ? parseInt(passMatch[1] ?? '0') : lines.length, type: 'number', status: 'success' },
        { label: 'Failed', value: failMatch ? parseInt(failMatch[1] ?? '0') : 0, type: 'number', status: failMatch ? 'error' : 'neutral' },
      ],
      rawOutput: output,
      showRaw: false,
    };
  }

  if (base === 'git' && command.includes('status')) {
    return {
      type: 'git-status',
      headline: lines.length > 1 ? 'Working tree has changes' : 'Working tree clean',
      status: lines.length > 1 ? 'info' : 'success',
      details: [
        { label: 'Changes', value: lines.length, type: 'number' },
      ],
      rawOutput: output,
      showRaw: false,
    };
  }

  return {
    type: 'general-success',
    headline: `Command completed successfully`,
    status: 'success',
    details: [
      { label: 'Command', value: command, type: 'text' },
      { label: 'Output Lines', value: lines.length, type: 'number' },
    ],
    rawOutput: output,
    showRaw: lines.length > 20,
  };
}

function buildDefaultOnboarding(skillLevel: SkillLevel): OnboardingState {
  return {
    isFirstRun: true,
    skillAssessment: {
      level: skillLevel,
      commandsUsed: 0,
      uniqueCommandsUsed: 0,
      conceptsLearned: [],
      lastAssessedAt: Date.now(),
    },
    completedTutorials: [],
    achievements: [],
    currentTutorial: undefined,
    preferences: {
      showWelcomeMessage: true,
      showContextualTips: skillLevel !== 'expert',
      showAchievements: true,
      autoSuggestTutorials: skillLevel === 'beginner' || skillLevel === 'intermediate',
    },
  };
}

function buildDefaultQuickActions(projectType?: string): QuickAction[] {
  const actions: QuickAction[] = [
    { id: 'qa-ls', label: 'List Files', description: 'Show files in current directory', icon: 'folder', category: 'files', command: 'ls -la', riskLevel: 'safe', requiresConfirmation: false, tags: ['files', 'list'] },
    { id: 'qa-git-status', label: 'Git Status', description: 'Check working tree status', icon: 'git', category: 'git', command: 'git status', riskLevel: 'safe', requiresConfirmation: false, tags: ['git', 'status'] },
    { id: 'qa-git-log', label: 'Recent Commits', description: 'View recent commit history', icon: 'history', category: 'git', command: 'git log --oneline -10', riskLevel: 'safe', requiresConfirmation: false, tags: ['git', 'log'] },
    { id: 'qa-git-diff', label: 'View Changes', description: 'Show uncommitted changes', icon: 'diff', category: 'git', command: 'git diff', riskLevel: 'safe', requiresConfirmation: false, tags: ['git', 'diff'] },
  ];

  if (projectType === 'node' || !projectType) {
    actions.push(
      { id: 'qa-npm-install', label: 'Install Dependencies', description: 'Install npm packages', icon: 'package', category: 'dependencies', command: 'npm install', riskLevel: 'safe', requiresConfirmation: false, tags: ['npm', 'install'] },
      { id: 'qa-npm-test', label: 'Run Tests', description: 'Execute test suite', icon: 'test', category: 'project', command: 'npm test', riskLevel: 'safe', requiresConfirmation: false, tags: ['npm', 'test'] },
      { id: 'qa-npm-build', label: 'Build Project', description: 'Build production bundle', icon: 'build', category: 'project', command: 'npm run build', riskLevel: 'safe', requiresConfirmation: false, tags: ['npm', 'build'] },
      { id: 'qa-npm-dev', label: 'Start Dev Server', description: 'Launch development server', icon: 'play', category: 'project', command: 'npm run dev', riskLevel: 'safe', requiresConfirmation: false, tags: ['npm', 'dev'] },
    );
  }

  return actions;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §4 FACTORY — createSmartTerminal
// ═══════════════════════════════════════════════════════════════════════════════

export function createSmartTerminal(config: SmartTerminalConfig): SmartTerminalHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  const state: InternalState = {
    name: config.name ?? 'Smart Terminal',
    shell: config.shell ?? 'zsh',
    colorScheme: config.colorScheme ?? 'dark',
    skillLevel: config.skillLevel ?? 'intermediate',
    enableHints: config.enableHints !== false,
    enablePreviews: config.enablePreviews !== false,
    enableDigest: config.enableDigest !== false,
    commandHistory: [],
    uniqueCommands: new Set(),
    commandCount: 0,
    sessionStart: Date.now(),
    onboarding: buildDefaultOnboarding(config.skillLevel ?? 'intermediate'),
    tutorialStep: 0,
  };

  function trackCommand(command: string): void {
    state.commandHistory.push(command);
    state.uniqueCommands.add(command.trim().split(/\s+/)[0] ?? command);
    state.commandCount++;

    if (state.commandHistory.length > 500) {
      state.commandHistory.splice(0, state.commandHistory.length - 250);
    }

    // Update skill assessment in onboarding
    state.onboarding = {
      ...state.onboarding,
      skillAssessment: {
        ...state.onboarding.skillAssessment,
        commandsUsed: state.commandCount,
        uniqueCommandsUsed: state.uniqueCommands.size,
        lastAssessedAt: Date.now(),
      },
    };
  }

  return Object.freeze({
    name: 'smart-terminal',

    getState(): InternalTerminalSnapshot {
      guardDestroyed();
      return {
        name: state.name,
        shell: state.shell,
        colorScheme: state.colorScheme,
        skillLevel: state.skillLevel,
        commandHistory: [...state.commandHistory],
        commandCount: state.commandCount,
        sessionStart: state.sessionStart,
      };
    },

    executeCommand(command: string): ExecuteResult {
      guardDestroyed();
      try {
        trackCommand(command);

        // Simulate execution — real impl would delegate to PTY
        const simulatedOutput = `Executed: ${command}\n[simulated output]`;
        const simulatedExitCode = 0;

        const digest = state.enableDigest
          ? buildDigest(command, simulatedOutput, simulatedExitCode)
          : { type: 'raw-output' as const, headline: command, status: 'neutral' as const, details: [], rawOutput: simulatedOutput, showRaw: true };

        const hints: CommandHint[] = [];
        if (state.enableHints) {
          const postHint = buildHint(command, 'post-success', state.skillLevel);
          if (postHint) hints.push(postHint);
        }

        const preview = state.enablePreviews
          ? buildPreview(command)
          : { type: 'none' as const, command, summary: '', details: [], affectedPaths: [], riskLevel: 'safe' as const, canDryRun: false };

        circuitStats = recordSuccess(circuitStats);
        return { digest, hints, preview };
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return {
          digest: { type: 'general-error', headline: 'Execution failed', status: 'error', details: [], rawOutput: '', showRaw: false },
          hints: [],
          preview: { type: 'none' as const, command, summary: '', details: [], affectedPaths: [], riskLevel: 'safe' as const, canDryRun: false },
        };
      }
    },

    getHint(command: string): CommandHint | null {
      guardDestroyed();
      if (!state.enableHints) return null;
      return buildHint(command, 'pre-execution', state.skillLevel);
    },

    getPreview(command: string): CommandPreview {
      guardDestroyed();
      return buildPreview(command);
    },

    translateNL(input: string): NLTranslation {
      guardDestroyed();
      for (const { pattern, command, confidence } of NL_PATTERNS) {
        const match = input.match(pattern);
        if (match) {
          let resolved = command;
          for (let i = 1; i < match.length; i++) {
            if (match[i]) resolved = resolved.replace(`$${i}`, match[i]!.trim());
          }
          circuitStats = recordSuccess(circuitStats);
          return { command: resolved, confidence };
        }
      }
      circuitStats = recordSuccess(circuitStats);
      return { command: input, confidence: 0.1 };
    },

    searchPalette(query: string): PaletteEntry[] {
      guardDestroyed();
      const q = query.toLowerCase();
      const scored: Array<{ entry: PaletteEntry; score: number }> = [];

      for (const cmd of COMMAND_DB) {
        const score = (cmd.command.includes(q) ? 3 : 0)
          + (cmd.description.toLowerCase().includes(q) ? 2 : 0)
          + (cmd.category.includes(q) ? 1 : 0);

        if (score > 0) {
          scored.push({
            entry: {
              id: `palette-${cmd.command}`,
              label: cmd.command,
              description: cmd.description,
              category: cmd.category as PaletteEntry['category'],
              command: cmd.command,
              keywords: [cmd.command, cmd.category],
              riskLevel: cmd.riskLevel ?? 'safe',
              frequency: 0,
              lastUsed: 0,
              isCustom: false,
            },
            score,
          });
        }
      }

      // Also search recent commands
      for (const histCmd of state.commandHistory.slice(-50)) {
        if (histCmd.toLowerCase().includes(q)) {
          scored.push({
            entry: {
              id: `history-${generateId('pal')}`,
              label: histCmd,
              description: 'Recent command',
              category: 'custom' as PaletteEntry['category'],
              command: histCmd,
              keywords: [histCmd],
              riskLevel: 'safe',
              frequency: 0,
              lastUsed: 0,
              isCustom: false,
            },
            score: 1,
          });
        }
      }

      return scored.sort((a, b) => b.score - a.score).slice(0, 20).map(s => s.entry);
    },

    getQuickActions(context?: { projectType?: string }): QuickAction[] {
      guardDestroyed();
      return buildDefaultQuickActions(context?.projectType);
    },

    digestOutput(command: string, output: string, exitCode: number): OutputDigest {
      guardDestroyed();
      return buildDigest(command, output, exitCode);
    },

    getOnboardingState(): OnboardingState {
      guardDestroyed();
      return state.onboarding;
    },

    advanceTutorial(command: string): { matched: boolean; explanation: string } {
      guardDestroyed();
      const tutorial = state.onboarding.currentTutorial;
      if (!tutorial) {
        return { matched: false, explanation: 'No active tutorial. Start one from the onboarding panel.' };
      }
      // Simulated tutorial progression
      state.tutorialStep++;
      return {
        matched: true,
        explanation: `Step ${state.tutorialStep} completed. Well done! The command "${command}" was correct.`,
      };
    },

    getSkillLevel(): SkillLevel {
      guardDestroyed();
      return state.skillLevel;
    },

    switchTheme(scheme: 'dark' | 'light'): void {
      guardDestroyed();
      state.colorScheme = scheme;
      circuitStats = recordSuccess(circuitStats);
    },

    stats(): CircuitStats {
      return { ...circuitStats, uptime: Date.now() - createdAt };
    },

    health(): CircuitHealthReport {
      if (destroyed) return buildHealthReport('smart-terminal', 'destroyed', 'Circuit destroyed', createdAt);
      const details = `${formatStatsDetails(circuitStats)}, Commands: ${state.commandCount}, Unique: ${state.uniqueCommands.size}`;
      return buildHealthReport('smart-terminal', healthFromStats(circuitStats), details, createdAt);
    },

    destroy(): void {
      destroyed = true;
    },
  });
}
